/**
 * Text normalisation shared by client and worker.
 *
 * Every comparison in VociFuchs goes through `norm`. It must stay a pure
 * function: the offline practice mode in the browser has to reach the same
 * verdict as the worker, otherwise a round submitted after a reconnect would
 * disagree with what the child already saw on screen.
 */

const TYPOGRAPHIC_APOSTROPHES = /[\u2018\u2019\u02bc\u055a\u0060\u00b4]/g;
const WHITESPACE_RUN = /\s+/g;
/** «cher/chère» and «cher / chère» are one word (issue #88). */
const SLASH = /\s*\/\s*/g;

/** Trim, collapse whitespace, space slashes, unify apostrophes, NFC, lowercase. */
export function norm(input: string): string {
  return input
    .normalize("NFC")
    .replace(TYPOGRAPHIC_APOSTROPHES, "'")
    .replace(WHITESPACE_RUN, " ")
    .replace(SLASH, " / ")
    .trim()
    .toLowerCase();
}

/** `norm` plus removal of all combining marks: école -> ecole. */
export function deaccent(input: string): string {
  return norm(input)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC");
}

/**
 * True when `a` and `b` differ by at most one insertion, deletion or
 * substitution. Linear scan instead of a full Levenshtein matrix — the worker
 * has a 10 ms CPU budget and we only ever ask about distance 1.
 */
export function withinDistanceOne(a: string, b: string): boolean {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;

  if (la === lb) {
    let diffs = 0;
    for (let i = 0; i < la; i++) {
      if (a[i] !== b[i] && ++diffs > 1) return false;
    }
    return diffs === 1;
  }

  // Exactly one length apart: walk both, allow a single skip in the longer one.
  const shorter = la < lb ? a : b;
  const longer = la < lb ? b : a;
  let i = 0;
  let j = 0;
  let skipped = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) {
      i++;
      j++;
      continue;
    }
    if (skipped) return false;
    skipped = true;
    j++;
  }
  return true;
}
